<template>
  <b-dropdown
    variant="white"
    auto-close="outside"
    ref="siteDropdown"
    class="d-inline"
    v-if="filteredIntegrations?.length > 0"
  >
    <template #button-content>
      <span class="integration-label">{{ conf.storeLabel() }} <i class="bx bx-chevron-down"/></span>
    </template>
    <div class="scrollable customer-block">
      <b-dropdown-item-button
        class="notify-item"
        v-for="integration in filteredIntegrations"
        :key="integration.id"
        :value="integration.label"
        @click="changeIntegration(integration)"
        :disabled="false"
      >
        <span class="icon-size me-3" v-if="conf.displayConnectorIcon">
          <img v-if="integration.connector?.icon_64 == null" v-static:src.trim="getConnectorIcon(integration)" />
          <img v-else-if="getConnectorIcon(integration)" :src="getConnectorIcon(integration)" />
        </span>

        <span class="align-middle">
          {{ integration.id === integration.label ? conf.storeLabel() : integration[conf.entityLabel] }}
        </span>
      </b-dropdown-item-button>
    </div>
  </b-dropdown>
  <span v-else>{{ conf.storeLabel() }}</span>
</template>
<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

const route = useRoute();
const router = useRouter();
const store = useStore();

const siteDropdown = ref(null);
const allIntegrations = ref([]);
const props = defineProps({
  integrationDropdown: {
    type: String,
    default: '',
  },
});
const defineConf = {
  destination: {
    displayConnectorIcon: true,
    storeEntityId: () => store.state.integration.integration.entity.id,
    storeLabel: () => store.state.integration.integration.entity.label,
    entityLabel: 'label',
  },
  source: {
    displayConnectorIcon: true,
    storeEntityId: () => store.state.integration.integration.entity.id,
    storeLabel: () => store.state.integration.integration.entity.label,
    entityLabel: 'label',
  },
  'data-cleansing': {
    displayConnectorIcon: false,
    storeEntityId: () => store.state.integration.cleansingTransformation.entity.id,
    storeLabel: () => store.state.integration.cleansingTransformation.entity.label,
    entityLabel: 'label',
  },
  enrich: {
    displayConnectorIcon: false,
    storeEntityId: () => store.state.enrichment.eventEnrichment.entity.id,
    storeLabel: () => store.state.enrichment.eventEnrichment.entity.name,
    entityLabel: 'name',
  },
  container: {
    displayConnectorIcon: true,
    storeEntityId: () => {
      // If the integration is a branch of another integration, return the parent_id
      if (store.state.integration.integration.entity?.model?.parent_id) {
        return store.state.integration.integration.entities.find(
          e => e.model?.id == store.state.integration.integration.entity?.model?.parent_id
        )?.id;
      }
      return store.state.integration.integration.entity.id;
    },
    storeLabel: () => {
      // return the label of the parent container if the integration is a branch of another integration
      if (store.state.integration.integration.entity?.model?.parent_id) {
        return store.state.integration.integration.entities.find(
          e => e.model?.id == store.state.integration.integration.entity?.model?.parent_id
        )?.label;
      }
      return store.state.integration.integration.entity.label;
    },
    entityLabel: 'label',
  },
};
const conf = computed(() => {
  return defineConf[props.integrationDropdown];
});
const filteredIntegrations = computed(() => {
  return allIntegrations.value.filter(e => e.id !== conf.value.storeEntityId());
});

onMounted(async () => {
  await setAllIntegrations();
});

const setAllIntegrations = async () => {
  if (props.integrationDropdown === 'destination') {
    const integrationList = await store.dispatch('integration/integration/findAll', {
      include: 'connector',
      filter: {
        types: 'destination',
      },
    });

    allIntegrations.value = integrationList.data;
  }

  if (props.integrationDropdown === 'source') {
    const integrationList = await store.dispatch('integration/integration/findAll', {
      include: 'connector,model',
      filter: {
        types: 'source',
      },
    });

    allIntegrations.value = integrationList.data;
  }

  if (props.integrationDropdown === 'data-cleansing') {
    const integrationList = await store.dispatch('integration/cleansingTransformation/findAll', {
      include: 'sources,sources.connector,sources.environment',
      filter: {
        status: 'active',
      },
    });

    allIntegrations.value = integrationList.data.filter(
      transformation => !(transformation.ref_id && transformation.operation === 'filter_event')
    );
  }

  if (props.integrationDropdown === 'enrich') {
    const integrationList = await store.dispatch('enrichment/eventEnrichment/findAll', {
      include: 'store,variables,sources,sources.connector,sources.model,sources.environment',
      filter: {
        status: 'active',
      },
    });

    allIntegrations.value = integrationList.data;
  }

  if (props.integrationDropdown === 'container') {
    const integrationList = await store.dispatch('integration/integration/findAll', {
      include: 'connector,model',
      filter: {
        types: 'source',
      },
    });

    // Filter out integrations that are not web containers
    const integrationWebContainerOnly = integrationList.data.filter(e => e.connector.template === 'containers');
    // Filter out integrations that are not branch of another integration
    allIntegrations.value = integrationWebContainerOnly.filter(e => !e?.model?.parent_id);
  }
};

const changeIntegration = async integration => {
  siteDropdown.value.hide();

  await navigateTo(integration);

  await setAllIntegrations();
};

const getConnectorIcon = integration => {
  return integration.connector?.icon_64 || integration.connector?.icon;
};

const navigateTo = async integration => {
  if (props.integrationDropdown === 'destination') return await navigateToDestination(integration);
  if (props.integrationDropdown === 'source' && integration?.connector?.template === 'containers')
    return await navigateToSourceV7(integration);
  if (props.integrationDropdown === 'source') return await navigateToSource(integration);
  if (props.integrationDropdown === 'data-cleansing') return await navigateToDataCleansing(integration);
  if (props.integrationDropdown === 'enrich') return await navigateToEnrich(integration);
  if (props.integrationDropdown === 'container') return await navigateToWebContainer(integration);
};

const navigateToDestination = async integration => {
  await Promise.all([
    store.dispatch('integration/integration/loadCurrent', {
      id: integration.id,
      include:
        'health,connector,connector.categories,universe,updater,creator,environment,storage_connector,segments,stream_columns,stream_columns.variable,stream_columns.modifiers,sources,sources.connector,sources.environment,sources.model,sources.destinations,sources.destinations.connector,alerts',
    }),
    store.dispatch('normalizedDatalayer/eventPropertyName/findAllCached'),
  ]);

  await router.push({
    name: 'destination-settings',
    params: { id: integration.id },
  });
};

const navigateToSourceV7 = async integration => {
  await router.push({
    name: 'container-web-edit-v7',
    params: { id: integration?.model?.id },
  });
};

const navigateToSource = async integration => {
  await Promise.all([
    store.dispatch('integration/integration/loadCurrent', {
      id: integration.id,
      include:
        'alerts,connector,connector.categories,storage_connector,universe,updater,creator,environment,destinations,destinations.connector,destinations.environment,destinations.updater,destinations.sources,destinations.sources.connector,destinations.sources.connector.categories,sources'
      }),
    store.dispatch('integration/template/loadCurrent', {
      id: integration.connector.id,
      include: 'categories',
    }),
    store.dispatch('integration/integration/findAll', {
      include: 'connector,environment,sources,sources.connector,model,updater',
      filter: {
        types: 'destination',
      },
    }),
  ]);

  await router.push({
    name: 'source-overview-tab',
    params: { id: integration.id },
  });
};

const navigateToWebContainer = async integration => {
  store.dispatch('integration/integration/loadCurrent', {
    id: integration.id,
    include: 'connector,model',
  });
  await store.dispatch('tms/webContainer/loadCurrent', integration.model.id);
  await router.push({
    name: route.name,
    params: { containerID: integration.model.id },
  });
};

const navigateToDataCleansing = async integration => {
  await store.dispatch('integration/cleansingTransformation/loadCurrent', {
    id: integration.id,
    include:
      'sources,sources.connector,sources.destinations,sources.environment,sources.model,sources.destinations.connector',
    rangeType: 'relative',
  });

  await router.push({
    name: 'edit',
    params: { id: integration.id },
  });
};

const navigateToEnrich = async integration => {
  if (integration.enrichment_type === 'datastore') {
    const promises = [
      store.dispatch('enrichment/eventEnrichment/loadCurrent', {
        id: integration.id,
        include: 'store,variables,sources,sources.connector,sources.model,sources.environment',
      }),
      store.commit('enrichment/eventEnrichment/setEnrichment_type', 'datastore')
    ];

    await Promise.all(promises);

    await router.push({
      name: 'event-enrichment-datastore-edit',
      params: { id: integration.id },
    });
  }

  if (integration.enrichment_type === 'storage_settings') {
    const promises = [
      store.dispatch('enrichment/eventEnrichment/loadCurrent', {
        id: integration.id,
        include: 'sources,sources.connector,sources.model,sources.environment,updater,creator',
      }),
      store.dispatch('dataGovernance/storageSettings/findAll'),
      store.commit('enrichment/eventEnrichment/setEnrichment_type', 'storage_settings'),
    ];

    await Promise.all(promises);

    await router.push({
      name: 'event-enrichment-custom-datastore-edit-settings',
      params: { id: integration.id },
    });
  }

  if (integration.enrichment_type === 'api') {
    const promises = [
      store.dispatch('normalizedDatalayer/eventTypeName/findAllCached', {
        filter: {
          categories: ['standard', 'datalayer', 'auto-discover'],
        },
      }),
      store.dispatch('enrichment/eventEnrichment/loadCurrent', {
        id: integration.id,
        include: 'sources,sources.connector,sources.model,sources.environment',
      }),
      store.commit('enrichment/eventEnrichment/setEnrichment_type', 'api'),
    ];

    await Promise.all(promises);

    await router.push({
      name: 'event-enrichment-api-edit-settings',
      params: { id: integration.id },
    });
  }
};
</script>

<style scoped lang="scss">
.integration-label {
  display: inline;
  font-size: 1.015625rem;
  font-weight: 600;
  color: var(--bs-body-color);
}

:deep(.dropdown-toggle) {
  padding: 0 !important;
  margin: 0 !important;
  border: none;
}

.icon-size {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f7f9fd;
  border: 1px solid #edeff5;
  padding: 6px;
  display: inline-flex;
  vertical-align: middle !important;
}
</style>
